import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

import { getAccountingFileName } from "../../../../../Utils";
import { downloadFileWithFallback } from "../../../../../Utils/DownloadUtils";
import ErrorDialog from "../../../../../Components/v2/ErrorDialog";
import LoadErrorState from "../../../../../Components/v2/LoadErrorState";
import { REPORT_TYPES } from "./utils";

/**
 * Piutang | Hutang, as the underline tabs on Pendapatan
 * (Transaksi Pendapatan | Uang Muka).
 */
export const ReportTypeTabs = ({ value, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className="border-b border-gray-200 px-4 sm:px-6 lg:px-8">
      <div
        role="tablist"
        aria-label={t("reports.partner.type")}
        className="-mb-px flex gap-6"
      >
        {REPORT_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            role="tab"
            aria-selected={value === type}
            onClick={() => onChange(type)}
            className={clsx(
              "-mb-px inline-flex items-center whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500",
              value === type
                ? "border-blue-600 text-blue-700"
                : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
            )}
          >
            {t(`reports.partner.types.${type}`)}
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Export through the report endpoint and download the file it writes to the
 * caller's private storage, as the other report pages do. `run(exportType)`
 * resolves to `{ key }`.
 */
export const useReportExport = (run) => {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);

  const exportAs = async (exportType) => {
    if (exporting) return;
    setExporting(true);
    try {
      const file = await run(exportType);
      if (file?.key) {
        await downloadFileWithFallback(
          file.key,
          undefined,
          getAccountingFileName,
        );
      }
    } catch (err) {
      setError(err);
    } finally {
      setExporting(false);
    }
  };

  const errorModal = error ? (
    <ErrorDialog open error={error} onClose={() => setError(null)} />
  ) : null;

  return { exportAs, exporting, errorModal };
};

/**
 * A report that failed to load, in the same block every accounting page uses,
 * with a way to ask again without reloading the page.
 */
export const LoadError = ({ error, onRetry }) => (
  <div className="rounded-lg border border-gray-200">
    <LoadErrorState error={error} onRetry={onRetry} />
  </div>
);

/** Dashed empty state, as Rekap PPN's. */
export const EmptyState = ({ title, description }) => (
  <div className="rounded-lg border border-dashed border-gray-300 px-6 py-10 text-center">
    <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
    {description && <p className="mt-1 text-sm text-gray-600">{description}</p>}
  </div>
);

export const TH =
  "px-3 py-2 text-left text-sm font-medium text-gray-900 whitespace-nowrap";
export const TH_NUM = `${TH} text-right`;
export const TD = "px-3 py-2 text-sm whitespace-nowrap";
export const TD_NUM = `${TD} text-right tabular-nums`;
